import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type Dispatch,
} from 'react';
import type { ProjectStructureView } from '@obraplan/core/application/use-cases/structure/get-project-structure';
import type { ProjectTakeoff } from '@obraplan/core/application/use-cases/takeoff/takeoff-use-cases';
import type { ProjectServiceItems } from '@obraplan/core/application/use-cases/takeoff/service-items-use-cases';
import type { RenamedImportedPiece } from '@obraplan/core/application/contracts/takeoff';
import { asId, type ProjectId } from '@obraplan/core/domain/shared/ids';
import { pieceAliasMap } from '@obraplan/core/domain/takeoff/imported-piece';
import { nomeDaPeca } from '@obraplan/core/domain/takeoff/planilha-por-peca';
import {
  guessItemKind,
  serviceKey,
  type ServiceCatalog,
} from '@obraplan/core/domain/takeoff/service-catalog';
import type { ProjectGeometry } from '@obraplan/core/application/use-cases/takeoff/geometry-use-cases';
import type { Service } from '@obraplan/core/domain/takeoff/service';
import { emptyCatalog } from '@obraplan/core/domain/takeoff/service-catalog';
import { guessAppliesTo, type AppliesTo } from '@obraplan/core/domain/takeoff/applies-to';
import type { MeasureBy } from '@obraplan/core/domain/takeoff/geometry';
import {
  apenasGeometria,
  catalogoDoServidor,
  catalogoParaServidor,
  desenhoDaObra,
  geometriaDoRascunho,
  geometriaParaRascunho,
  itensDoServidor,
  itensParaServidor,
  lancamentosDoServidor,
  montarWorkspace,
  pavimentosDaObra,
  semOsItens,
  temItensNoRascunho,
  temItensNoServidor,
  type ItensParaGravar,
} from './do-servidor';
import { mudancasNosAprovados, recusaDaAba, type MudancaNoAprovado } from './desenho-travado';
import type { ServicosAprovados } from './servicos-aprovados';
import {
  emptyWorkspace,
  lembrarOsLados,
  workspaceReducer,
  type PlanDraft,
  type TypologyDraft,
  type WorkspaceAction,
  type WorkspaceDraft,
} from './typology-draft';

/**
 * O que ainda **não** tem tabela, e o navegador como banco do resto.
 *
 * Encolheu três vezes. Na fatia 1 da Fase 14 a identidade saiu — desenho, tipologia e
 * posição viraram registro da obra. Na fatia 3 saiu a **geometria**: ambiente, parede,
 * vão, pilar e viga passaram a viver no servidor. Na Fase 24 saíram os **itens do
 * serviço** — o catálogo importado, a aba de cada tipologia e o que foi digitado nela
 * (RN-249) — e a **prancha**: o arquivo e a escala são da obra, e a imagem renderizada
 * fica só na memória da tela. O que sobra aqui é o que ainda não tem tabela: a
 * divergência do apto.
 *
 * A junção acontece em `montarWorkspace`: o rascunho lido daqui entra, os dados da obra
 * entram, e sai a `WorkspaceDraft` com a forma que as telas sempre leram. Quem consome
 * não soube da mudança.
 *
 * ## A gravação
 *
 * Não é a cada tecla. A montagem é um envelope — a geometria vai inteira, numa transação
 * (ver `SaveProjectGeometry`) — e mandá-la a cada dígito do comprimento de uma parede
 * seria mandar a tipologia inteira seis vezes para escrever "3,40". Então espera-se o
 * dedo parar: **um segundo e meio** sem mudança, e sobe.
 *
 * ## Quem já tinha desenho no navegador
 *
 * Existe gente com a tipologia montada no `localStorage` de antes desta fatia. A primeira
 * leitura do servidor vem vazia para essas obras, e deixá-la vencer apagaria o trabalho
 * delas na hora. Então a regra da primeira vez é explícita: **servidor vazio e rascunho
 * com desenho não é conflito, é migração** — o rascunho fica, e a gravação seguinte o
 * leva para lá. Com desenho dos dois lados, o servidor manda; ele é a verdade agora. O
 * rascunho não diz de que obra é, então só migra o que aponta a obra aberta (`desenhoDaObra`).
 */

/**
 * Chave nova de propósito.
 *
 * O rascunho da versão anterior guardava torres e tipologias que agora vêm do servidor,
 * com ids que não existem em obra nenhuma. Lê-lo produziria uma bancada meio de um mundo
 * e meio do outro; mudar a chave o descarta de uma vez, que é o que foi decidido.
 */
const STORAGE_KEY = 'obraplan.rascunho-tipologia.v2';

/**
 * Onde a prancha de antes da Fase 24 espera para subir, uma vez.
 *
 * Ela morava dentro do rascunho, e o rascunho deixou de levá-la: o arquivo agora é da
 * obra. Mas quem traçou sobre ela antes desta fase só a tem aqui — o PDF original não foi
 * guardado em lugar nenhum —, e perdê-la na primeira gravação do rascunho seria perder a
 * única folha que o traçado dele conhece. Então ela muda de chave na primeira leitura e
 * fica esperando: `usePranchaDoPavimento` a sobe para o desenho certo e a esquece.
 */
const CHAVE_DA_PRANCHA_ANTIGA = 'obraplan.prancha-a-subir.v1';

/**
 * Tira a prancha de dentro do rascunho e a guarda na chave própria.
 *
 * Nesta ordem — o rascunho encolhe antes de a prancha ser gravada de novo —, o espaço
 * que ela ocupava é liberado antes de ser pedido outra vez: a cota do `localStorage` é
 * justamente o que uma prancha grande deixa apertado. Se ainda assim não couber, o
 * rascunho volta como estava, com a prancha dentro, e a próxima abertura tenta de novo.
 */
function separarPranchaAntiga(bruto: string, salvo: Partial<WorkspaceDraft>): void {
  if (salvo.plan === undefined) return;
  const { plan, ...semPrancha } = salvo;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(semPrancha));
    localStorage.setItem(CHAVE_DA_PRANCHA_ANTIGA, JSON.stringify(plan));
  } catch {
    try {
      localStorage.setItem(STORAGE_KEY, bruto);
    } catch {
      // Nem voltar coube. O rascunho ilegível já é tratado como perdido na leitura.
    }
  }
}

/** A prancha de antes da Fase 24, se ainda não subiu — ver `CHAVE_DA_PRANCHA_ANTIGA`. */
export function lerPranchaAntiga(): PlanDraft | undefined {
  try {
    const bruto = localStorage.getItem(CHAVE_DA_PRANCHA_ANTIGA);
    if (bruto === null) return undefined;
    const plan = JSON.parse(bruto) as Partial<PlanDraft>;
    // O `canvas` sempre gravou `data:image/...`; outra coisa aqui não é a prancha de ontem.
    return typeof plan.image === 'string' &&
      plan.image.startsWith('data:image/') &&
      typeof plan.width === 'number' &&
      typeof plan.height === 'number'
      ? (plan as PlanDraft)
      : undefined;
  } catch {
    return undefined;
  }
}

/** A prancha de antes subiu, ou o servidor já tinha uma: não há mais o que guardar. */
export function esquecerPranchaAntiga(): void {
  try {
    localStorage.removeItem(CHAVE_DA_PRANCHA_ANTIGA);
  } catch {
    // Sem `localStorage` não há o que esquecer.
  }
}

/**
 * Quanto silêncio conta como "terminei", em milissegundos.
 *
 * Um segundo e meio: longo o bastante para não subir a montagem inteira entre dois
 * dígitos de uma medida, curto o bastante para quem fecha a aba logo depois de mexer não
 * perder o que mexeu.
 */
const ESPERA_PARA_GRAVAR = 1500;

/**
 * As gravações que esperam o silêncio, por chave. Quem fecha a aba (ou sai da tela) antes de
 * `ESPERA_PARA_GRAVAR` perderia a última edição: o `pagehide` e o desmonte descarregam o que
 * ainda espera, na hora.
 */
type Pendentes = Map<string, () => void>;

/** Agenda a gravação para depois da pausa; devolve a limpeza do efeito. */
function agendarGravacao(pendentes: Pendentes, chave: string, enviar: () => void): () => void {
  const relogio = setTimeout(() => {
    pendentes.delete(chave);
    enviar();
  }, ESPERA_PARA_GRAVAR);
  pendentes.set(chave, () => {
    clearTimeout(relogio);
    pendentes.delete(chave);
    enviar();
  });
  return () => {
    clearTimeout(relogio);
  };
}

/** O servidor respondeu com desenho, ou com a obra que ainda não tem nenhum? */
function temDesenho(geometria: ProjectGeometry): boolean {
  // O desenho da Fundação pode ser só estacas, blocos, baldrames e o radier, sem ambiente
  // nem parede (Fase 27), o do andar pode começar pela laje ou pela escada, o da Contenção
  // pode ser só muros e pilares de contenção, e a obra pode ter tipos cadastrados antes da
  // primeira estaca: tudo isso também é desenho.
  return (
    geometria.rooms.length > 0 ||
    geometria.walls.length > 0 ||
    (geometria.pieces?.length ?? 0) > 0 ||
    (geometria.pileTypes?.length ?? 0) > 0 ||
    (geometria.pileCapTypes?.length ?? 0) > 0 ||
    (geometria.elementTypes?.length ?? 0) > 0 ||
    (geometria.pileCaps?.length ?? 0) > 0 ||
    (geometria.groundBeams?.length ?? 0) > 0 ||
    (geometria.slabs?.length ?? 0) > 0 ||
    (geometria.retainingWalls?.length ?? 0) > 0 ||
    (geometria.retainingColumns?.length ?? 0) > 0 ||
    (geometria.stairs?.length ?? 0) > 0
  );
}

/**
 * O catálogo guardado antes de o item ter quantidade.
 *
 * A primeira versão da importação só guardava a quantidade do **serviço**; a do item veio
 * depois, junto com a tela de lançamento. Um rascunho gravado no meio disso tem itens sem
 * `quantityBySheet`, e o tipo diz que ele existe — porque para o código que o constrói ele
 * sempre existe. O que vem do `localStorage` não é construído por esse código: é JSON de
 * ontem, e o `as` que o converte é uma promessa que ninguém verificou.
 *
 * Curar aqui, na fronteira, é o que mantém o domínio livre de `?.` defensivo em cada
 * leitura. Do outro lado disto há a planilha de alguém, importada e conferida — descartar
 * o rascunho seria fazer a pessoa refazer o trabalho por uma coluna que falta.
 */
/** A peça pelo nome, ou nada — o pendente não leva o campo (ver `guessAppliesTo`). */
function elementoPeloNome(
  measureBy: MeasureBy,
  nome: string,
  disciplina: string,
): { readonly appliesTo?: AppliesTo } {
  const appliesTo = guessAppliesTo(measureBy, nome, disciplina);
  return appliesTo === undefined ? {} : { appliesTo };
}

function curarCatalogo(catalog: ServiceCatalog): ServiceCatalog {
  return {
    ...catalog,
    disciplines: catalog.disciplines.map((disciplina) => ({
      ...disciplina,
      services: disciplina.services.map((servico) => ({
        ...servico,
        // A chave sempre com a disciplina (RN-164). O serviço que veio só do servidor
        // era gravado com o nome normalizado, e CONCRETAGEM de vigas, pilares e lajes
        // dividiam uma chave só. Refazê-la aqui cura o rascunho já gravado.
        key: serviceKey(disciplina.name, servico.name),
        // Rascunho de antes de o sistema guardar quem escolheu a medida. Ausente vira
        // `PALPITE`, e não `DECIDIDO`: **falta de registro não é decisão**. Chamar de
        // decidido o que ninguém confirmou congelaria o palpite antigo para sempre,
        // justamente nos serviços que nunca foram conferidos.
        measureOrigin: servico.measureOrigin ?? 'PALPITE',
        // Rascunho de antes do "Aplica a" (Fase 24): a peça sai do nome, como a migração
        // `0036` fez no banco. O que o nome não resolve fica pendente, e não mede.
        ...(servico.appliesTo === undefined
          ? elementoPeloNome(servico.measureBy, servico.name, disciplina.name)
          : {}),
        quantityBySheet: servico.quantityBySheet ?? {},
        items: servico.items.map((item) => ({
          ...item,
          quantityBySheet: item.quantityBySheet ?? {},
          // Rascunho de antes de o item ter classe. Vira palpite pelo mesmo motivo da
          // medida do serviço: falta de registro não é decisão, e o palpite de hoje
          // reconhece a mão de obra que o de ontem não reconhecia.
          kind:
            item.kind ??
            guessItemKind({
              disciplineName: disciplina.name,
              serviceName: servico.name,
              description: item.description,
              ...(item.unit === undefined ? {} : { unit: item.unit }),
            }),
          kindOrigin: item.kindOrigin ?? 'PALPITE',
        })),
      })),
    })),
  };
}

/**
 * A tipologia gravada antes de a estrutura existir (Fase 15).
 *
 * Mesmo caso do catálogo acima: o tipo promete `elements` e `elementSegments`, e o JSON de
 * ontem não os tem. Sem curar aqui, a primeira leitura da tela chamaria `.filter` em
 * `undefined` e derrubaria a bancada inteira de quem já tinha desenho montado — por causa
 * de dois campos vazios.
 */
function curarTipologia(typology: TypologyDraft): TypologyDraft {
  // A recusa é da ação que falhou, e não do rascunho: a de ontem não é de nenhuma ferramenta
  // de hoje (o "muro precisa de dois pontos" que reaparecia no painel da Escada).
  const { lastError: _recusaAntiga, ...semRecusa } = typology;
  return {
    ...semRecusa,
    elements: typology.elements ?? [],
    elementSegments: typology.elementSegments ?? [],
  };
}

/**
 * As chaves de serviço que `curarCatalogo` refaz, e o que as quantidades gravadas sob
 * elas viram.
 *
 * `itemQuantities` e a linha-sombra do apto apontam o serviço pela chave; refazer a chave
 * do catálogo sem mover o que a usa deixaria a quantidade órfã — lançada, gravada e
 * invisível. Só entra no mapa o que mudou, e só se move o que estava sob uma chave antiga.
 */
function chavesRefeitas(catalog: ServiceCatalog): ReadonlyMap<string, string> {
  const mapa = new Map<string, string>();
  for (const disciplina of catalog.disciplines) {
    for (const servico of disciplina.services) {
      const nova = serviceKey(disciplina.name, servico.name);
      if (servico.key !== nova) mapa.set(servico.key, nova);
    }
  }
  return mapa;
}

function moverChaves<T>(
  registro: Readonly<Record<string, T>>,
  refeitas: ReadonlyMap<string, string>,
): Readonly<Record<string, T>> {
  const movido: Record<string, T> = {};
  for (const [chave, valor] of Object.entries(registro)) {
    const separador = chave.indexOf('»');
    const antiga = separador === -1 ? chave : chave.slice(0, separador);
    const nova = refeitas.get(antiga);
    movido[nova === undefined ? chave : `${nova}${chave.slice(separador)}`] = valor;
  }
  return movido;
}

export function carregarRascunho(): WorkspaceDraft {
  try {
    const bruto = localStorage.getItem(STORAGE_KEY);
    if (bruto !== null) {
      const salvo = JSON.parse(bruto) as Partial<WorkspaceDraft>;
      separarPranchaAntiga(bruto, salvo);
      const guardadas = salvo.typologies;
      if (guardadas !== undefined && guardadas.length > 0) {
        const refeitas =
          salvo.catalog === undefined ? new Map<string, string>() : chavesRefeitas(salvo.catalog);
        const comChavesNovas = (typology: TypologyDraft): TypologyDraft =>
          refeitas.size === 0 || typology.itemQuantities === undefined
            ? typology
            : { ...typology, itemQuantities: moverChaves(typology.itemQuantities, refeitas) };
        const sombras =
          salvo.unitShadows === undefined || refeitas.size === 0
            ? salvo.unitShadows
            : Object.fromEntries(
                Object.entries(salvo.unitShadows).map(([chave, sombra]) => [
                  chave,
                  { ...sombra, lines: moverChaves(sombra.lines, refeitas) },
                ]),
              );
        // A parede de antes do índice passa a lembrar o lado aqui também (RN-244): quem
        // abre a obra antes de o servidor responder já vê o desenho que vai ser gravado.
        return lembrarOsLados({
          // A estrutura não é mais guardada: ela vem da obra a cada carga, e uma cópia
          // aqui só teria como envelhecer.
          towers: [],
          // A prancha não entra: a da mesa vem do servidor (Fase 24), e a de antes esperou
          // na chave própria — `separarPranchaAntiga`, logo acima.
          ...(salvo.catalog === undefined ? {} : { catalog: curarCatalogo(salvo.catalog) }),
          // As divisas entre tipologias são da bancada, e não de nenhuma delas: sem
          // carregá-las aqui, cada recarga da página desmancharia o andar tipo.
          ...(salvo.partyWalls === undefined ? {} : { partyWalls: salvo.partyWalls }),
          ...(salvo.partyOpenings === undefined ? {} : { partyOpenings: salvo.partyOpenings }),
          // O pilar e a viga das divisas moram na bancada pelo mesmo motivo das paredes:
          // sem carregá-los aqui, cada recarga da página devolveria o bloco que o
          // concreto ocupa, e o total subiria sozinho.
          ...(salvo.partyElements === undefined ? {} : { partyElements: salvo.partyElements }),
          ...(salvo.partyElementSegments === undefined
            ? {}
            : { partyElementSegments: salvo.partyElementSegments }),
          ...(salvo.partyLabels === undefined ? {} : { partyLabels: salvo.partyLabels }),
          // A divergência do apto é do rascunho, e não de nenhuma tipologia: sem
          // carregá-la aqui, o que o 1204 faz de diferente sumia a cada recarga — e
          // sumia calado, porque a leitura entende ausência como "segue a tipologia".
          ...(sombras === undefined ? {} : { unitShadows: sombras }),
          typologies: guardadas.map((typology) => comChavesNovas(curarTipologia(typology))),
          activeId: salvo.activeId ?? guardadas[0]!.unitTypeId,
        });
      }
    }
  } catch {
    // Rascunho ilegível é rascunho perdido, e não tela quebrada.
  }
  return emptyWorkspace();
}

/**
 * A bancada montada: a identidade do servidor, a geometria do navegador.
 *
 * Recebe o que as duas leituras da obra trouxeram. Enquanto elas ainda não chegaram
 * (`undefined`), devolve o rascunho cru — a tela mostra o esqueleto de carregamento, e
 * não uma bancada vazia que pareceria uma obra sem nada cadastrado.
 *
 * O que vai para o `localStorage` é `apenasGeometria(...)`: gravar de volta o que veio do
 * servidor criaria uma segunda cópia da identidade para divergir.
 */
export interface DoServidor {
  /** O desenho lido, com a revisão dele quando o servidor a devolve (`0062`). */
  readonly geometria?: ProjectGeometry & { readonly revision?: number };
  /**
   * A obra aberta, e a obra para a qual `geometria` foi lida (07/10/2026).
   *
   * A tela não é remontada ao trocar de obra, e o `useLive` segura a leitura antiga até a
   * nova chegar. Sem saber de quem é cada coisa, a bancada assentava o desenho uma vez só —
   * na primeira obra — e, ao trocar, gravava na obra nova o que estava na mesa antes de o
   * desenho dela chegar: um desenho vazio, que apagava as peças, os ambientes e as paredes da
   * obra de destino (achado do teste de 07/10). Com as duas, cada obra é assentada quando o
   * desenho **dela** chega, e nada se grava antes disso.
   */
  readonly obra?: string;
  readonly geometriaDe?: string;
  /**
   * A obra do `quantitativo`, ou `null` enquanto o da obra aberta não chegou (28/10/2026).
   *
   * O desenho é repartido pelas tipologias da mesa, e são as do quantitativo. Ao trocar para a
   * obra recém-duplicada, o desenho dela chegava antes do quantitativo: nenhuma peça achava a
   * sua tipologia, todas ficavam de fora, e a gravação seguinte apagava o desenho inteiro da
   * cópia. Com a obra dita, o desenho só se assenta, e só se grava, com o quantitativo da obra
   * aberta na mesa. Ausente é quem não diz — o desenho sem quantitativo dos testes —, e vale o
   * de antes.
   */
  readonly quantitativoDe?: string | null;
  /** O cadastro de serviços da obra — os dela mais os globais (RN-007). */
  readonly servicos?: readonly Service[];
  /**
   * Os itens do serviço da obra (Fase 24, fatia 3) — lidos junto com `servicos`, na mesma
   * consulta, para que os dois sejam sempre da mesma obra.
   */
  readonly itens?: ProjectServiceItems;
  /**
   * Grava o desenho sobre a revisão que a bancada leu (`baseRevision`). Devolve o que o
   * servidor respondeu — a revisão nova, ou a recusa por o desenho ter mudado em outro lugar
   * — para a bancada encadear a próxima gravação e parar de gravar sozinha no conflito.
   */
  readonly salvarGeometria?: (
    geometria: ProjectGeometry,
    baseRevision?: number,
  ) => void | Promise<ResultadoDaGravacaoDoDesenho | void>;
  /** Relê o desenho da obra no servidor — o "Recarregar o desenho" do conflito. */
  readonly reler?: () => void;
  /** Devolve a gravação, quando há: os itens esperam por ela antes de subir. */
  readonly salvarCatalogo?: (disciplinas: DisciplinasParaGravar) => void | Promise<unknown>;
  /**
   * Grava o envelope dos itens na obra dele. Responde se gravou — só o que o servidor
   * confirmou deixa de ser reenviado.
   */
  readonly salvarItens?: (projectId: ProjectId, envelope: ItensParaGravar) => Promise<boolean>;
  /**
   * Os serviços aprovados no orçamento da obra (Fase 33, RN-439): o desenho que muda a
   * quantidade de um deles não sobe. `undefined` enquanto a leitura não chega — a bancada grava
   * como antes, e o servidor segura o que é dele.
   */
  readonly aprovados?: ServicosAprovados;
  /** Diz a recusa de um clique: a aba de referência que mexeria num serviço aprovado. */
  readonly avisarRecusa?: (texto: string) => void;
}

/** O que a gravação do desenho respondeu à bancada. */
export interface ResultadoDaGravacaoDoDesenho {
  /** A revisão depois de gravar — a base da próxima gravação. */
  readonly revision?: number;
  /** O desenho mudou em outra aba ou por outra pessoa: nada foi gravado. */
  readonly conflito?: boolean;
}

/**
 * O desenho da obra aberta mudou em outro lugar (409 `TAKEOFF.GEOMETRY_CONFLICT`)?
 *
 * Enquanto `emConflito`, a gravação automática está parada: a mesa partiu de uma revisão que
 * já não é a do servidor, e gravá-la apagaria o que a outra aba gravou. `recarregar` relê o
 * desenho e **descarta a mesa** — a última alteração, a que não subiu, se perde.
 */
export interface ConflitoDoDesenho {
  readonly emConflito: boolean;
  readonly recarregar: () => void;
}

/**
 * A mudança no desenho que mexeria num serviço aprovado (Fase 33, RN-439).
 *
 * Enquanto `mudancas` não está vazia, o desenho da obra aberta não sobe — nem esta mudança nem
 * as seguintes, porque ele vai inteiro. O aviso some sozinho quando o desenho volta a dar a
 * quantidade de antes; `desfazer` põe de volta o último desenho gravado.
 */
export interface DesenhoTravado {
  readonly mudancas: readonly MudancaNoAprovado[];
  readonly desfazer: () => void;
}

const NENHUMA_MUDANCA: readonly MudancaNoAprovado[] = [];

/**
 * O nome novo de uma peça da planilha, já gravado no servidor (20/10/2026, RN-457). A tela o
 * entrega à bancada, que refaz o mesmo na mesa — ver `useWorkspaceDraft`.
 */
export type RenomearPecaNaMesa = (renomeada: RenamedImportedPiece) => void;

/** O desenho gravado com a peça desenhada renomeada, só na tipologia dela. */
function desenhoComPecaRenomeada(
  desenho: ProjectGeometry,
  unitTypeId: string,
  de: string,
  para: string,
): ProjectGeometry {
  const antigo = nomeDaPeca(de);
  const lista = <P extends { readonly name: string; readonly unitTypeId: string }>(
    pecas: readonly P[] | undefined,
  ): readonly P[] | undefined =>
    pecas?.map((peca) =>
      String(peca.unitTypeId) === unitTypeId && nomeDaPeca(peca.name) === antigo
        ? { ...peca, name: para }
        : peca,
    );
  const pieces = lista(desenho.pieces);
  const pileCaps = lista(desenho.pileCaps);
  const groundBeams = lista(desenho.groundBeams);
  const slabs = lista(desenho.slabs);
  const retainingWalls = lista(desenho.retainingWalls);
  const retainingColumns = lista(desenho.retainingColumns);
  const stairs = lista(desenho.stairs);
  return {
    ...desenho,
    ...(pieces === undefined ? {} : { pieces }),
    ...(pileCaps === undefined ? {} : { pileCaps }),
    ...(groundBeams === undefined ? {} : { groundBeams }),
    ...(slabs === undefined ? {} : { slabs }),
    ...(retainingWalls === undefined ? {} : { retainingWalls }),
    ...(retainingColumns === undefined ? {} : { retainingColumns }),
    ...(stairs === undefined ? {} : { stairs }),
  };
}

export type DisciplinasParaGravar = ReturnType<typeof catalogoParaServidor>;

/** O que o despacho e a gravação leem na hora, e não do momento em que foram criados. */
interface NaMesa {
  readonly rascunho: WorkspaceDraft;
  readonly estrutura: ProjectStructureView | undefined;
  readonly quantitativo: ProjectTakeoff | undefined;
  readonly aprovados: ServicosAprovados | undefined;
  readonly avisarRecusa: ((texto: string) => void) | undefined;
}

/**
 * A troca da aba de referência que muda a quantidade de um item de serviço aprovado — a
 * recusa, ou `undefined` quando ela passa (decisão do Gilvan, 19/10/2026). A aba é da
 * tipologia, como a Estrutura, mas a troca é um clique de alguém: o "não" vem na hora, com o
 * antes × depois, e não num aviso que espera alguém desfazer.
 */
function recusaDaTrocaDeAba(
  action: Extract<WorkspaceAction, { readonly type: 'SET_TYPOLOGY_SHEET' }>,
  { rascunho, estrutura, quantitativo, aprovados }: NaMesa,
): string | undefined {
  if (aprovados === undefined || aprovados.size === 0) return undefined;
  const montar = (draft: WorkspaceDraft): WorkspaceDraft =>
    estrutura === undefined || quantitativo === undefined
      ? draft
      : montarWorkspace(draft, estrutura, quantitativo);
  const mudancas = mudancasNosAprovados(
    montar(rascunho),
    montar(workspaceReducer(rascunho, action)),
    aprovados,
    new Set([String(action.unitTypeId)]),
  );
  return mudancas.length === 0 ? undefined : recusaDaAba(action.sheetName, mudancas);
}

/** A apresentação das tipologias da obra ao rascunho — ver `SYNC_TYPOLOGIES`. */
function sincronizar(quantitativo: ProjectTakeoff): WorkspaceAction {
  const pavimentos = pavimentosDaObra(quantitativo);
  return {
    type: 'SYNC_TYPOLOGIES',
    unitTypes: quantitativo.unitTypes.map((unitType) => ({
      unitTypeId: unitType.id,
      name: unitType.name,
      kind: unitType.kind,
      // O mesmo pavimento que a montagem mostra: é no rascunho que a peça se confere.
      floorKind: pavimentos.get(unitType.id) ?? 'TIPO',
    })),
  };
}

/** As tipologias da obra — o escopo da quantidade digitada no envelope dos itens. */
function tipologiasDa(quantitativo: ProjectTakeoff): ReadonlySet<string> {
  return new Set(quantitativo.unitTypes.map((unitType) => String(unitType.id)));
}

/**
 * As leituras da tela já são todas da obra dos itens?
 *
 * A espinha diz de quem é pelas próprias peças (`projectId` do desenho e da tipologia); a
 * estrutura, pela disciplina própria da obra — o serviço desta obra numa disciplina que a
 * estrutura não lista é sinal de estrutura da obra anterior. A obra sem peça nenhuma não
 * tem como estar atrasada: não há o que pendurar errado nela.
 */
function daMesmaObra(
  projectId: ProjectId,
  servicos: readonly Service[],
  estrutura: ProjectStructureView,
  quantitativo: ProjectTakeoff,
): boolean {
  const espinhaDaObra =
    quantitativo.unitTypes.every((unitType) => unitType.projectId === projectId) &&
    quantitativo.floorPlans.every((plano) => plano.projectId === projectId);
  const disciplinas = new Set(estrutura.availableDisciplines.map((d) => String(d.id)));
  const estruturaDaObra = servicos
    .filter((service) => service.projectId === projectId)
    .every((service) => disciplinas.has(String(service.disciplineId)));
  return espinhaDaObra && estruturaDaObra;
}

export function useWorkspaceDraft(
  estrutura?: ProjectStructureView,
  quantitativo?: ProjectTakeoff,
  servidor: DoServidor = {},
): readonly [
  WorkspaceDraft,
  Dispatch<WorkspaceAction>,
  boolean,
  ConflitoDoDesenho,
  DesenhoTravado,
  RenomearPecaNaMesa,
] {
  const [rascunho, dispatch] = useReducer(workspaceReducer, undefined, carregarRascunho);
  const { geometria, servicos, itens, salvarGeometria, salvarCatalogo, salvarItens } = servidor;
  const { obra, geometriaDe, quantitativoDe, reler, aprovados, avisarRecusa } = servidor;
  // O despacho da tela e a gravação que espera a pausa leem o que está na mesa agora.
  const naMesa = useRef<NaMesa>({ rascunho, estrutura, quantitativo, aprovados, avisarRecusa });
  naMesa.current = { rascunho, estrutura, quantitativo, aprovados, avisarRecusa };
  /**
   * De que obra é o desenho que está na mesa — `undefined` antes do primeiro. Só depois de
   * assentar o desenho da obra aberta faz sentido gravar por cima dela.
   */
  const assentadoPara = useRef<string | undefined>(undefined);
  /** A obra aberta, sem chave: a de quem chama, ou a única que esta bancada conhece. */
  const obraAberta = obra ?? geometriaDe ?? '';
  /** As tipologias da mesa já são as da obra aberta? Ver `quantitativoDe`. */
  const espinhaDaObra = quantitativoDe === undefined || quantitativoDe === obraAberta;
  /**
   * A revisão do desenho de cada obra: a que a leitura trouxe, e depois a que cada gravação
   * devolveu. Vai em `baseRevision`; se o servidor a recusar, o desenho mudou em outro lugar.
   * Por obra, e não uma só: a edição pendente de uma obra sobe depois da troca para outra.
   */
  const revisoes = useRef(new Map<string, number>());
  /** As obras cujo desenho mudou em outro lugar — nelas a gravação automática está parada. */
  const obrasEmConflito = useRef(new Set<string>());
  const [conflitoDe, setConflitoDe] = useState<string | undefined>(undefined);
  /**
   * A gravação do desenho em voo, por obra. A seguinte espera a anterior: com duas no ar, a
   * segunda sairia com a revisão de antes da primeira e o servidor a recusaria sem haver
   * conflito algum.
   */
  const gravacoesDoDesenho = useRef(new Map<string, Promise<void>>());
  /** O próximo assentamento vale mesmo que o servidor responda sem desenho (recarga). */
  const forcarAssentamento = useRef(false);
  /**
   * O último desenho de cada obra que subiu, ou que veio do servidor (Fase 33, RN-439). É o
   * "antes" da conta do serviço aprovado, e o que o "Desfazer a mudança" põe de volta. Sem ele
   * — o desenho que só este navegador tinha, na migração —, a primeira gravação passa e vira ele.
   */
  const desenhosGravados = useRef(new Map<string, ProjectGeometry>());
  const [travaDe, setTravaDe] = useState<
    { readonly obra: string; readonly mudancas: readonly MudancaNoAprovado[] } | undefined
  >(undefined);
  const catalogoAssentado = useRef(false);

  /**
   * De que obra são os itens que estão na mesa.
   *
   * A tela não é remontada ao trocar de obra, e o `useLive` segura a leitura antiga até a
   * nova chegar: sem saber de quem é o que está na mesa, a gravação levaria os itens de uma
   * obra para a outra. Com ele, cada envelope sobe para a obra que o produziu, e a obra nova
   * é assentada inteira quando chegar.
   */
  const itensDe = useRef<string | undefined>(undefined);
  /** O último envelope que o servidor confirmou — ou que acabou de vir dele. */
  const ultimoEnvelope = useRef<string | undefined>(undefined);
  /**
   * As gravações em voo, em fila. Os itens esperam o cadastro: o serviço novo precisa
   * existir lá antes que um item possa apontá-lo.
   */
  const gravacaoDoCatalogo = useRef<Promise<unknown>>(Promise.resolve());
  const gravacaoDosItens = useRef<Promise<unknown>>(Promise.resolve());
  /**
   * O servidor já é o dono dos itens desta mesa? Até ser, o `localStorage` continua
   * guardando a cópia — é ela que sobe na migração, e perdê-la antes de o servidor
   * confirmar seria perder a planilha de quem fechou a aba no meio.
   */
  const [itensNoServidor, setItensNoServidor] = useState(false);

  /**
   * As gravações que ainda esperam a pausa. Descarregadas ao fechar a aba (`pagehide`,
   * `beforeunload`) e ao desmontar a tela. Este efeito vem **antes** dos que agendam: no
   * desmonte, o React roda as limpezas na ordem em que foram declaradas, e a descarga precisa
   * ver as pendentes antes de os outros limparem os relógios.
   */
  const pendentes = useRef<Pendentes>(new Map());
  // Trocar de obra descarrega o que ainda esperava a pausa: cada envio guardado leva o
  // `salvar…` da obra que o produziu, e a última edição da obra anterior não se perde.
  const obraDaMesa = useRef(obraAberta);
  if (obraDaMesa.current !== obraAberta) {
    obraDaMesa.current = obraAberta;
    for (const enviar of [...pendentes.current.values()]) enviar();
  }
  useEffect(() => {
    const mapa = pendentes.current;
    const descarregar = () => {
      for (const descarga of [...mapa.values()]) descarga();
    };
    window.addEventListener('pagehide', descarregar);
    window.addEventListener('beforeunload', descarregar);
    return () => {
      window.removeEventListener('pagehide', descarregar);
      window.removeEventListener('beforeunload', descarregar);
      descarregar();
    };
  }, []);

  const workspace = useMemo(
    () =>
      estrutura === undefined || quantitativo === undefined
        ? rascunho
        : montarWorkspace(rascunho, estrutura, quantitativo),
    [rascunho, estrutura, quantitativo],
  );

  /**
   * As duas listas de tipologia se encontram aqui.
   *
   * `montarWorkspace` monta o que a tela **mostra**, e procura a geometria de cada
   * tipologia da obra por `unitTypeId`. Quem guarda essa geometria é o `rascunho`, que é
   * o que o reducer escreve — e ele abria com uma tipologia de id sorteado, que não é o
   * de nenhuma tipologia da obra. Todo ambiente criado ia parar nela, e a montagem
   * procurava pelo id do servidor e não achava nada: o ambiente existia no rascunho e não
   * aparecia na tela. Sem erro, porque erro não houve — só duas listas que nunca se
   * apresentaram.
   *
   * Depende do quantitativo, e não do rascunho: a ação devolve o mesmo objeto quando o
   * conjunto de ids já bate, então a rebusca que segue cada escrita não regrava nada.
   */
  useEffect(() => {
    if (quantitativo === undefined) return;
    dispatch(sincronizar(quantitativo));
  }, [quantitativo]);

  /**
   * O desenho do servidor entra — uma vez, e só a geometria.
   *
   * Depende de `geometria` e não de `rascunho`: assentar a cada mudança do rascunho
   * desfaria, no quadro seguinte, tudo o que a pessoa acabou de digitar.
   *
   * Servidor vazio com rascunho cheio é **migração**, e não conflito: quem montou a
   * tipologia antes desta fatia tem o desenho só no navegador, e deixar o vazio vencer
   * apagaria o trabalho dela na hora em que a tela abrisse. O rascunho fica, e a gravação
   * seguinte o leva para o servidor — mas só o que é da obra aberta (`desenhoDaObra`): o
   * rascunho é um só para todas as obras, e a divisa da anterior subia junto (30/10/2026).
   */
  useEffect(() => {
    if (geometria === undefined || assentadoPara.current === obraAberta) return;
    // A leitura que ainda é da obra anterior não assenta a nova.
    if (geometriaDe !== undefined && geometriaDe !== obraAberta) return;
    // Nem o desenho que chegou antes do quantitativo da obra: repartido entre as tipologias da
    // obra anterior, ele cairia inteiro fora da mesa (28/10/2026).
    if (!espinhaDaObra) return;
    assentadoPara.current = obraAberta;
    // A revisão do desenho que está entrando é a base da próxima gravação.
    if (geometria.revision === undefined) revisoes.current.delete(obraAberta);
    else revisoes.current.set(obraAberta, geometria.revision);
    // As tipologias da obra já na mesa: o quantitativo que chega no mesmo quadro que o desenho
    // só seria apresentado depois deste efeito, e o desenho iria para as tipologias de antes.
    const mesa =
      quantitativo === undefined ? rascunho : workspaceReducer(rascunho, sincronizar(quantitativo));
    // Na recarga do conflito o servidor manda mesmo vazio: a mesa velha não pode sobreviver.
    if (temDesenho(geometria) || forcarAssentamento.current) {
      dispatch({ type: 'HYDRATE_GEOMETRY', workspace: geometriaParaRascunho(mesa, geometria) });
      desenhosGravados.current.set(obraAberta, geometria);
    } else {
      // Servidor vazio: da mesa fica só o que é desta obra (30/10/2026). A divisa e os tipos da
      // obra anterior moram na bancada, e não saem com as tipologias dela.
      const desenho = geometriaDoRascunho(mesa);
      const daObra = quantitativo === undefined ? desenho : desenhoDaObra(desenho, quantitativo);
      if (daObra !== desenho) {
        dispatch({ type: 'HYDRATE_GEOMETRY', workspace: geometriaParaRascunho(mesa, daObra) });
      }
      // A migração: o desenho que fica na mesa ainda não subiu, e não há "antes" para ele.
      if (temDesenho(daObra)) desenhosGravados.current.delete(obraAberta);
      else desenhosGravados.current.set(obraAberta, geometria);
    }
    forcarAssentamento.current = false;
    // Sem `rascunho` nas dependências de propósito: ele é lido no instante em que o
    // servidor responde, e observá-lo faria o efeito correr de novo a cada tecla.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geometria, geometriaDe, obraAberta, espinhaDaObra, quantitativo]);

  /**
   * A gravação, depois de o dedo parar.
   *
   * A geometria sobe **inteira**, num envelope — é o que `SaveProjectGeometry` recebe —, e
   * mandá-la a cada tecla mandaria a tipologia seis vezes para escrever "3,40". Um segundo
   * e meio de silêncio é o sinal de que a pessoa terminou o que estava fazendo.
   *
   * Só depois de assentar: gravar antes de saber o que o servidor tem apagaria, com uma
   * bancada recém-aberta, o desenho de quem fechou a aba ontem.
   */
  useEffect(() => {
    if (salvarGeometria === undefined || assentadoPara.current !== obraAberta) return;
    // Montada com as tipologias de outra obra, a mesa não tem o desenho desta (28/10/2026).
    if (!espinhaDaObra) return;
    // Desenho que mudou em outro lugar não se grava por cima (07/10/2026).
    if (obrasEmConflito.current.has(obraAberta)) return;
    const obraDoEnvio = obraAberta;
    return agendarGravacao(pendentes.current, 'geometria', () => {
      const desenho = geometriaDoRascunho(workspace);
      // O desenho que muda a quantidade de um serviço aprovado não sobe (RN-439).
      const mudancas = mudancasDoDesenho(obraDoEnvio, workspace);
      // O aviso muda depois, e não aqui: a troca de obra descarrega esta gravação no meio da
      // renderização, e lá o estado não se mexe.
      void Promise.resolve().then(() => {
        setTravaDe((atual) =>
          mudancas.length > 0
            ? { obra: obraDoEnvio, mudancas }
            : atual?.obra === obraDoEnvio
              ? undefined
              : atual,
        );
      });
      if (mudancas.length > 0) return;
      desenhosGravados.current.set(obraDoEnvio, desenho);
      gravarDesenho(obraDoEnvio, salvarGeometria, desenho);
    });
    // `gravarDesenho` e `mudancasDoDesenho` só leem refs e o `setState`: não precisam estar
    // nas dependências.
  }, [workspace, salvarGeometria, obraAberta, conflitoDe, espinhaDaObra]);

  /**
   * O que a mesa mudou nos serviços aprovados desde o último desenho gravado (RN-439).
   *
   * Os dois lados são a mesma mesa — a mesma Estrutura, o mesmo cadastro, os mesmos itens —, e
   * só o desenho é o de antes: o que muda a conta é a mudança no desenho, e nada mais (D6).
   */
  const mudancasDoDesenho = (
    obraDoEnvio: string,
    mesa: WorkspaceDraft,
  ): readonly MudancaNoAprovado[] => {
    const gravado = desenhosGravados.current.get(obraDoEnvio);
    const { aprovados: aprovadosAgora } = naMesa.current;
    if (gravado === undefined || aprovadosAgora === undefined || aprovadosAgora.size === 0) {
      return NENHUMA_MUDANCA;
    }
    return mudancasNosAprovados(geometriaParaRascunho(mesa, gravado), mesa, aprovadosAgora);
  };

  /**
   * Sobe o desenho sobre a revisão que a bancada conhece, uma gravação por vez em cada obra.
   *
   * O servidor recusa a base que já não é a atual (409): daí em diante a bancada **não grava
   * mais sozinha** nessa obra, e `conflito.recarregar` é a saída. A falha de rede não é
   * conflito — quem grava já a disse —, e a revisão segue a de antes.
   */
  const gravarDesenho = (
    obraDoEnvio: string,
    salvar: NonNullable<DoServidor['salvarGeometria']>,
    desenho: ProjectGeometry,
  ): void => {
    const registrar = (resultado: ResultadoDaGravacaoDoDesenho | void): void => {
      if (resultado === undefined) return;
      if (resultado.conflito === true) {
        obrasEmConflito.current.add(obraDoEnvio);
        setConflitoDe(obraDoEnvio);
      } else if (resultado.revision !== undefined) {
        revisoes.current.set(obraDoEnvio, resultado.revision);
      }
    };
    // Chama `salvar` na hora; só devolve algo a esperar quando ele devolve uma promessa.
    const enviar = (): Promise<void> | undefined => {
      if (obrasEmConflito.current.has(obraDoEnvio)) return undefined;
      try {
        const retorno = salvar(desenho, revisoes.current.get(obraDoEnvio));
        if (!(retorno instanceof Promise)) return undefined;
        // A falha de quem grava já foi dita; a fila só segue.
        return retorno.then(registrar, () => undefined);
      } catch {
        return undefined;
      }
    };
    const anterior = gravacoesDoDesenho.current.get(obraDoEnvio);
    // Ocioso, sai na hora (e de forma síncrona); com outra em voo, entra na fila.
    const atual = anterior === undefined ? enviar() : anterior.then(enviar);
    if (atual === undefined) return;
    gravacoesDoDesenho.current.set(obraDoEnvio, atual);
    void atual.then(() => {
      if (gravacoesDoDesenho.current.get(obraDoEnvio) === atual) {
        gravacoesDoDesenho.current.delete(obraDoEnvio);
      }
    });
  };

  /**
   * "Recarregar o desenho": relê o desenho da obra e o assenta por cima da mesa. A alteração
   * que o servidor recusou — e o que mais houver na mesa — se perde, e é por isso que a tela
   * avisa antes. Até a leitura chegar, a obra volta a "não assentada" e nada se grava.
   */
  const recarregar = useCallback(() => {
    obrasEmConflito.current.delete(obraAberta);
    setConflitoDe(undefined);
    // O desenho relido é o "antes" novo: a mudança que travava sai junto com a mesa.
    setTravaDe(undefined);
    forcarAssentamento.current = true;
    assentadoPara.current = undefined;
    reler?.();
  }, [obraAberta, reler]);
  const conflito = useMemo<ConflitoDoDesenho>(
    () => ({ emConflito: conflitoDe !== undefined && conflitoDe === obraAberta, recarregar }),
    [conflitoDe, obraAberta, recarregar],
  );

  /**
   * "Desfazer a mudança": o último desenho gravado volta para a mesa (decisão do Gilvan,
   * 19/10/2026). A bancada não tem Ctrl+Z, e é o caminho curto para sair do aviso. Sai junto o
   * que mais mudou depois da última gravação — normalmente só a mudança que travou.
   */
  const desfazer = useCallback(() => {
    const gravado = desenhosGravados.current.get(obraAberta);
    setTravaDe(undefined);
    if (gravado === undefined) return;
    dispatch({
      type: 'HYDRATE_GEOMETRY',
      workspace: geometriaParaRascunho(naMesa.current.rascunho, gravado),
    });
  }, [obraAberta]);
  const travado = useMemo<DesenhoTravado>(
    () => ({
      mudancas:
        travaDe !== undefined && travaDe.obra === obraAberta ? travaDe.mudancas : NENHUMA_MUDANCA,
      desfazer,
    }),
    [travaDe, obraAberta, desfazer],
  );

  /**
   * O nome novo da peça da planilha, que o servidor acabou de gravar (RN-457): a mesa refaz o
   * mesmo — as quantidades, a peça desenhada de mesmo nome e a lembrança do nome da planilha —,
   * e a próxima gravação dos itens e do desenho sobe o nome novo, e não o antigo. O desenho
   * gravado, o "antes" do serviço aprovado (RN-439), muda junto: sem isso, a peça desenhada com
   * o nome velho voltaria a contar ao lado da importada, e a conta acusaria mudança onde não
   * houve. A revisão que o renomear fez andar é a base da próxima gravação do desenho.
   */
  const renomearPeca = useCallback<RenomearPecaNaMesa>(
    (renomeada) => {
      dispatch({
        type: 'RENAME_IMPORTED_PIECE',
        unitTypeId: asId<'UnitType'>(renomeada.unitTypeId),
        from: renomeada.previousName,
        to: renomeada.pieceName,
        aliases: pieceAliasMap(renomeada.aliases),
      });
      if (renomeada.drawnPieces === 0) return;
      const gravado = desenhosGravados.current.get(obraAberta);
      if (gravado !== undefined) {
        desenhosGravados.current.set(
          obraAberta,
          desenhoComPecaRenomeada(
            gravado,
            renomeada.unitTypeId,
            renomeada.previousName,
            renomeada.pieceName,
          ),
        );
      }
      if (renomeada.geometryRevision !== undefined) {
        revisoes.current.set(
          obraAberta,
          Math.max(revisoes.current.get(obraAberta) ?? 0, renomeada.geometryRevision),
        );
      }
    },
    [obraAberta],
  );

  /**
   * O despacho que a tela recebe: a troca da aba de referência passa antes pela conta do
   * serviço aprovado, e a recusa é dita na hora (`avisarRecusa`). O resto vai direto.
   */
  const despachar = useCallback<Dispatch<WorkspaceAction>>((action) => {
    if (action.type === 'SET_TYPOLOGY_SHEET') {
      const recusa = recusaDaTrocaDeAba(action, naMesa.current);
      if (recusa !== undefined) {
        naMesa.current.avisarRecusa?.(recusa);
        return;
      }
    }
    dispatch(action);
  }, []);

  /**
   * O cadastro de serviços do servidor entra — uma vez, e por merge.
   *
   * Depende de `servicos` e da disciplina que os nomeia: o serviço gravado guarda
   * `disciplineId`, e o catálogo do rascunho fala em nome de disciplina. Quem traduz é a
   * estrutura, que já veio para a tela.
   *
   * Servidor sem serviço nenhum com catálogo importado no navegador é migração, como na
   * geometria: o rascunho fica, e a gravação seguinte o leva.
   */
  useEffect(() => {
    // Com os itens no servidor, quem assenta o catálogo é o efeito dos itens, abaixo: a
    // medida e o item entram juntos, de uma leitura só da mesma obra.
    if (salvarItens !== undefined) return;
    if (servicos === undefined || estrutura === undefined || catalogoAssentado.current) return;
    catalogoAssentado.current = true;
    if (servicos.length === 0) return;

    const nomes = new Map(
      estrutura.availableDisciplines.map((disciplina) => [String(disciplina.id), disciplina.name]),
    );
    dispatch({
      type: 'HYDRATE_CATALOG',
      catalog: catalogoDoServidor(rascunho.catalog ?? emptyCatalog(), servicos, (id) =>
        nomes.get(String(id)),
      ),
    });
    // Sem `rascunho` nas dependências, pelo motivo do assentamento da geometria.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [servicos, estrutura, salvarItens]);

  /**
   * O catálogo e os itens do servidor entram — uma vez por obra (Fase 24, fatia 3).
   *
   * **O servidor manda nos itens** (RN-249): o que o navegador guardava deles era cópia, e
   * a leitura da obra troca o que estiver na mesa — inclusive por nada, quando a obra não
   * tem item nenhum. A medida e a espessura seguem o merge de `catalogoDoServidor`.
   *
   * ## A migração única
   *
   * Quem importou a planilha antes desta fatia tem os itens, a aba de cada tipologia e o
   * que digitou **só no `localStorage`**. Na primeira obra aberta, com o servidor vazio e o
   * rascunho cheio, o rascunho fica — e a gravação seguinte o leva inteiro. Depois que o
   * servidor confirma, o navegador para de guardá-lo.
   *
   * Só a **primeira** obra da sessão pode migrar, e só com o que veio do `localStorage`:
   * o rascunho é um só para todas as obras, e trocar de obra com a mesa cheia levaria os
   * itens de uma para a outra como se fossem migração.
   *
   * O envelope do que acabou de chegar vira o "último gravado": abrir a tela não regrava
   * a obra, e duas abas abertas não se atropelam só por terem sido abertas.
   */
  useEffect(() => {
    if (salvarItens === undefined || itens === undefined) return;
    if (servicos === undefined || estrutura === undefined || quantitativo === undefined) return;
    if (itensDe.current === String(itens.projectId)) return;
    // Numa troca de obra, as três leituras chegam em tempos diferentes. Assentar os itens
    // da obra nova sobre a estrutura ou as tipologias da antiga penduraria decisão em
    // tipologia errada — e a gravação seguinte levaria a mistura para lá. Espera-se a
    // leitura que ainda é da outra obra chegar.
    if (!daMesmaObra(itens.projectId, servicos, estrutura, quantitativo)) return;
    const primeira = itensDe.current === undefined;
    itensDe.current = String(itens.projectId);
    catalogoAssentado.current = true;

    const nomes = new Map(
      estrutura.availableDisciplines.map((disciplina) => [String(disciplina.id), disciplina.name]),
    );
    const base = primeira ? (rascunho.catalog ?? emptyCatalog()) : emptyCatalog();
    const comServicos =
      servicos.length === 0
        ? base
        : catalogoDoServidor(base, servicos, (id) => nomes.get(String(id)));

    if (primeira && !temItensNoServidor(itens) && temItensNoRascunho(rascunho)) {
      dispatch({ type: 'HYDRATE_CATALOG', catalog: comServicos });
      ultimoEnvelope.current = undefined;
      return;
    }

    const lancamentos = lancamentosDoServidor(itens);
    const daEspinha = new Set(quantitativo.unitTypes.map((unitType) => String(unitType.id)));
    // A tipologia que decidiu alguma coisa e a espinha ainda não trouxe entra também, com
    // nome provisório — a identidade vem da espinha quando ela chegar (`montarWorkspace`).
    // Deixá-la de fora perderia a decisão dela na mesa, e a gravação seguinte a apagaria.
    const soNaLeitura = itens.unitTypes
      .filter((tipologia) => !daEspinha.has(String(tipologia.unitTypeId)))
      .map((tipologia) => ({
        id: tipologia.unitTypeId,
        name: 'Tipologia',
        kind: 'APARTAMENTO' as const,
      }));
    const acao: WorkspaceAction = {
      type: 'HYDRATE_ITEMS',
      catalog: itensDoServidor(comServicos, itens, servicos),
      typologies: [...quantitativo.unitTypes, ...soNaLeitura].map((unitType) => {
        const decidido = lancamentos[String(unitType.id)];
        return {
          unitTypeId: unitType.id,
          name: unitType.name,
          kind: unitType.kind,
          itemQuantities: decidido?.itemQuantities ?? {},
          itemReasons: decidido?.itemReasons ?? {},
          serviceQuantities: decidido?.serviceQuantities ?? {},
          ...(decidido?.sheetName === undefined ? {} : { sheetName: decidido.sheetName }),
          // As peças importadas (Fase 28): só quem o servidor devolve com peça ganha o campo.
          ...(decidido?.pieceQuantities === undefined
            ? {}
            : { pieceQuantities: decidido.pieceQuantities }),
          ...(decidido?.pieceNameAliases === undefined
            ? {}
            : { pieceNameAliases: decidido.pieceNameAliases }),
        };
      }),
    };
    // O mesmo caminho que a bancada vai percorrer — a apresentação das tipologias e o
    // assentamento —, feito aqui de antemão para saber que envelope ele produz.
    const assentado = workspaceReducer(workspaceReducer(rascunho, sincronizar(quantitativo)), acao);
    ultimoEnvelope.current = JSON.stringify(
      itensParaServidor(
        montarWorkspace(assentado, estrutura, quantitativo),
        tipologiasDa(quantitativo),
      ),
    );
    dispatch(acao);
    setItensNoServidor(true);
    // Sem `rascunho` nas dependências, pelo motivo do assentamento da geometria.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itens, servicos, estrutura, quantitativo, salvarItens]);

  /**
   * A gravação do cadastro, com a mesma pausa da geometria.
   *
   * Depende do **catálogo**, e não da bancada inteira: mexer numa parede não tem por que
   * remandar quarenta serviços.
   */
  const catalogo = workspace.catalog;
  useEffect(() => {
    if (salvarCatalogo === undefined || !catalogoAssentado.current || catalogo === undefined) {
      return;
    }
    // Com os itens no servidor, o catálogo da mesa é o da obra cujos itens chegaram: trocar de
    // obra não pode mandar o cadastro da anterior para a nova antes de a leitura dela chegar.
    if (salvarItens !== undefined && obra !== undefined && itensDe.current !== obra) return;
    return agendarGravacao(pendentes.current, 'catalogo', () => {
      // Guardada para os itens esperarem: o serviço novo precisa existir no servidor antes
      // que um item o aponte. A falha já foi dita por quem grava; aqui ela só não trava a fila.
      gravacaoDoCatalogo.current = Promise.resolve(
        salvarCatalogo(catalogoParaServidor(catalogo)),
      ).catch(() => undefined);
    });
  }, [catalogo, salvarCatalogo, salvarItens, obra]);

  /**
   * A gravação dos itens, com a mesma pausa — e só do que mudou.
   *
   * O envelope é montado na hora de gravar e comparado com o último que o servidor
   * confirmou: mexer numa parede refaz a bancada, e sem a comparação cada parede remandaria
   * duzentos itens.
   *
   * Vai para a obra **de quem o produziu** (`itensDe`), e não para a que estiver aberta
   * quando o relógio disparar: trocar de obra no meio da pausa não pode levar os itens de
   * uma para a outra.
   *
   * Espera a gravação do cadastro — declarada antes, com a mesma pausa, e por isso sempre
   * na frente — e a anterior dos itens: duas gravações cruzadas poriam a mais velha por
   * último.
   */
  useEffect(() => {
    if (salvarItens === undefined || quantitativo === undefined) return;
    const projectId = itensDe.current;
    if (projectId === undefined) return;
    return agendarGravacao(pendentes.current, 'itens', () => {
      const envelope = itensParaServidor(workspace, tipologiasDa(quantitativo));
      const texto = JSON.stringify(envelope);
      if (texto === ultimoEnvelope.current) return;
      const cadastro = gravacaoDoCatalogo.current;
      gravacaoDosItens.current = Promise.all([cadastro, gravacaoDosItens.current])
        .then(() => salvarItens(projectId as ProjectId, envelope))
        .then((gravou) => {
          if (!gravou) return;
          ultimoEnvelope.current = texto;
          setItensNoServidor(true);
        })
        .catch(() => undefined);
    });
  }, [workspace, quantitativo, salvarItens]);

  useEffect(() => {
    try {
      // Depois que o servidor passou a ser o dono dos itens, o navegador guarda só o que
      // ainda não tem tabela — a geometria que falta e a divergência do apto.
      const guardar = itensNoServidor
        ? semOsItens(apenasGeometria(rascunho))
        : apenasGeometria(rascunho);
      // E nunca a prancha: a imagem da mesa são megabytes que o servidor já guarda, e o
      // `localStorage` que ela lotava é o mesmo que guarda o resto do rascunho.
      const { plan: _doServidor, ...semPrancha } = guardar;
      // E nunca a recusa: ela é da ação que falhou, e gravada reapareceria, depois de
      // recarregar, no painel de outra ferramenta.
      const semRecusas = {
        ...semPrancha,
        typologies: semPrancha.typologies.map(({ lastError: _recusa, ...tipologia }) => tipologia),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(semRecusas));
    } catch {
      // Cota cheia não pode derrubar a montagem em andamento.
    }
  }, [rascunho, itensNoServidor]);

  return [
    workspace,
    despachar,
    estrutura !== undefined && quantitativo !== undefined,
    conflito,
    travado,
    renomearPeca,
  ];
}
